import { TAX_INVOICE_THRESHOLD_MINOR } from "@stb/jurisdiction-au"
import {
  Camera,
  Copy,
  Inbox,
  Percent,
  ScanLine,
  ShieldCheck,
  Sparkles,
  Store,
  Wrench,
  Briefcase,
} from "lucide-react"

import { TRIAL_DAYS } from "@/brand/plan-numbers"

import { ReceiptIllustration } from "../../components/home/home-art"
import { ListMock, ScanHeroMock } from "../../components/landing/product-art"
import { type ProductPageContent, withIcons } from "../../components/landing/product-page"
import { STORAGE_SENTENCE } from "../pages/trust-facts"
import { RECEIPT_EXAMPLE as ex } from "./example-numbers"

/** Copy source: docs/design/landing/copy-products.md (/receipt-scanner). Only features that ship today. */
const ATO_TAX_INVOICES =
  "https://www.ato.gov.au/businesses-and-organisations/gst-excise-and-indirect-taxes/gst/tax-invoices"
/** Last time a person opened the ATO page and compared it with this copy. */
export const RECEIPT_CHECKED = "29 October 2026"
const threshold = `A$${(TAX_INVOICE_THRESHOLD_MINOR / 100).toFixed(2)}`

export const RECEIPT_SEO = {
  title: "Receipt scanner app with GST checks for traders",
  description:
    "Photograph a receipt or upload a PDF. We read the supplier, date, total and GST, and you confirm before it counts. Free plan available.",
}

const COPY: ProductPageContent = {
  path: "/receipt-scanner",
  name: "Receipt scanner",
  tag: "Receipts and expenses",
  reviewed: "28 October 2026",
  h1: "A receipt scanner that checks the GST for you",
  lead: "Photograph a receipt or upload a PDF. We read the supplier, date, total and GST. You confirm each one before it becomes an expense.",
  answer: {
    text: `For GST purposes you generally need a tax invoice to claim a credit on a purchase over ${threshold} (GST inclusive, 2026–27). We read each receipt, flag purchases over that amount that are missing invoice details, and keep the original file.`,
    sources: [{ label: "ATO, tax invoices", href: ATO_TAX_INVOICES }],
    checked: RECEIPT_CHECKED,
  },
  cta: { label: "Start free trial", href: "/login" },
  secondaryCta: { label: "See pricing", href: "/pricing" },
  heroNote: "AI can misread a receipt, so nothing counts until you confirm it.",
  stats: [
    { value: threshold, label: "tax invoice check" },
    { value: "0 to 100%", label: "business-use share" },
    { value: "Review list", label: "for unclear receipts" },
    { value: "Originals", label: "kept for you to view" },
  ],
  stepsTitle: "Photo in, checked expense out",
  steps: [
    { title: "Capture", body: "Take a photo or drop a PDF on the website." },
    {
      title: "Review",
      body: `Anything unclear, such as a purchase over ${threshold} with no ABN, or GST that does not match the total, waits in your review list.`,
    },
    {
      title: "Confirm",
      body: "Set the business-use share and confirm. The expense is only booked once you say so.",
    },
  ],
  example: {
    title: "Two example receipts",
    caption: "Two example receipts",
    columns: ["Example receipt", "Total", "GST"],
    rows: [
      { header: ex.merchant1, cells: [ex.total1, ex.gst1] },
      { header: ex.merchant2, cells: [ex.total2, ex.gst2] },
    ],
    note: `Example only. GST is one eleventh of a GST-inclusive total. Every receipt waits in the review list until you confirm it. Estimate only.`,
  },
  included: [
    {
      title: "Fields read for you",
      body: "Supplier, ABN, date, total and GST. A receipt with fields we could not read clearly is flagged for you to check.",
    },
    {
      title: "A review list, not guesses",
      body: "Low-confidence or odd receipts wait for you. Nothing is booked silently.",
    },
    {
      title: "GST input check",
      body: `A flag when a purchase over ${threshold} is missing tax invoice details.`,
    },
    {
      title: "Business-use share",
      body: "Set 0 to 100% per expense.",
    },
    {
      title: "Duplicate warning",
      body: "The same file, or the same ABN, invoice number, amount and date, is flagged.",
    },
    {
      title: "Originals kept",
      body: "The original photo or PDF stays available to view.",
    },
  ],
  limits: {
    title: "AI helps, you decide",
    body: "AI can misread handwriting, faded paper or a crumpled receipt. Fields stay editable and nothing counts until you confirm it. Kestly gives estimates, not tax advice, and does not lodge anything with the ATO.",
  },
  who: [
    {
      title: "Tradies and mobile services",
      body: "Bunnings, parts and fuel receipts piling up in the ute.",
    },
    {
      title: "Contractors and freelancers",
      body: "Software, equipment and travel receipts in one list.",
    },
    {
      title: "Small shops and market traders",
      body: "A shoebox of paper receipts turned into a list you can review.",
    },
  ],
  trust: {
    title: "Your originals stay with you",
    body: `${STORAGE_SENTENCE} Receipts stay available to view. Nothing counts until you confirm it. See where your data lives on the trust page.`,
  },
  plan: {
    rows: ["aiReads"],
    note: "Expenses are part of the business plans; the mileage plan has no receipts.",
  },
  faqTitle: "Receipt scanner questions",
  faq: [
    {
      question: "Is the scan accurate?",
      answer:
        "It is usually right on clean printed receipts. We flag a receipt with fields we could not read clearly and ask you to check it. We do not quote an accuracy figure.",
    },
    {
      question: "Do I need an ABN?",
      answer: "Yes. Expenses are part of the business plans. The mileage plan has no receipts.",
    },
    {
      question: "Does it lodge anything with the ATO?",
      answer: "No. Kestly helps you organise receipts; you or your tax agent lodge.",
    },
  ],
  related: [
    { label: "Logbook method: when it pays off", href: "/logbook-method" },
    { label: "Invoicing", href: "/invoicing" },
    { label: "Mileage tracker", href: "/mileage-tracker" },
    { label: "How it works", href: "/how-it-works" },
    { label: "Pricing", href: "/pricing" },
    { label: "Where your data lives", href: "/trust" },
  ],
  band: {
    title: "Stop losing receipts",
    description: `Try it free for ${TRIAL_DAYS} days. No card needed.`,
  },
}

const STEP_ART = [
  <ReceiptIllustration key="capture" />,
  <ListMock
    key="review"
    rows={[
      {
        title: ex.merchant2,
        sub: `${ex.total2} · No ABN on the receipt`,
        badge: "To review",
        tone: "warning",
      },
      {
        title: ex.merchant1,
        sub: `${ex.total1} · GST ${ex.gst1}`,
        badge: "Ready",
        tone: "success",
      },
    ]}
  />,
  <ListMock
    key="confirm"
    rows={[
      {
        title: "Tools and equipment",
        sub: `GST ${ex.gst1} · Business use 100%`,
        badge: "Confirmed",
        tone: "success",
      },
      {
        title: "Office supplies",
        sub: `GST ${ex.gst2} · Business use 80%`,
        badge: "Confirmed",
        tone: "success",
      },
    ]}
  />,
]

/** Copy above; icons and pictures (T854) are presentation only. */
export const RECEIPT_SCANNER: ProductPageContent = {
  ...COPY,
  heroCard: <ScanHeroMock />,
  stats: withIcons(COPY.stats, [Percent, Inbox, Percent, ShieldCheck]),
  steps: COPY.steps.map((step, i) => ({ ...step, art: STEP_ART[i], artOnMobile: i > 0 })),
  included: withIcons(COPY.included, [ScanLine, Inbox, Percent, Percent, Copy, Camera]),
  limits: { ...COPY.limits, icon: Sparkles },
  who: withIcons(COPY.who, [Wrench, Briefcase, Store]),
}
